import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from './Login';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const DIAS = [
  { key: 'seg', label: 'Seg' },
  { key: 'ter', label: 'Ter' },
  { key: 'qua', label: 'Qua' },
  { key: 'qui', label: 'Qui' },
  { key: 'sex', label: 'Sex' },
  { key: 'sab', label: 'Sáb' },
  { key: 'dom', label: 'Dom' },
];

const TONES = [
  { key: 'simpatica', label: 'Simpática', desc: 'Amigável e acolhedora' },
  { key: 'formal',    label: 'Formal',    desc: 'Profissional e direta' },
  { key: 'animada',   label: 'Animada',   desc: 'Entusiasmada e divertida' },
  { key: 'tranquila', label: 'Tranquila', desc: 'Calma e serena' },
];

// ─── STYLES ───────────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 48px;
  padding-bottom: 6rem;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  padding: 1.2rem 1.25rem 1.5rem;
  color: white;
  h1 { margin: 0; font-size: 1.15rem; font-weight: 700; }
  p  { margin: 0.2rem 0 0; font-size: 0.78rem; opacity: 0.85; }
`;

const Content = styled.div`padding: 1rem 1.25rem;`;

const Section = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1.2rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
`;

const SectionTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const Field = styled.div`
  margin-bottom: 0.9rem;
  label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.3rem;
  }
  input, select {
    width: 100%;
    border: 1.5px solid #e5e7eb;
    border-radius: 0.6rem;
    padding: 0.6rem 0.8rem;
    font-size: 0.88rem;
    color: #1f2937;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s;
    &:focus { border-color: #334155; }
  }
`;

const Row = styled.div`display: flex; gap: 0.6rem;`;

const DayChips = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const DayChip = styled.button`
  width: 40px; height: 36px;
  border-radius: 0.5rem;
  border: 1.5px solid ${p => p.active ? '#334155' : '#e5e7eb'};
  background: ${p => p.active ? '#334155' : 'white'};
  color: ${p => p.active ? 'white' : '#6b7280'};
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
`;

const ToneGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`;

const ToneCard = styled.button`
  border: 1.5px solid ${p => p.active ? '#334155' : '#e5e7eb'};
  background: ${p => p.active ? '#f1f5f9' : 'white'};
  border-radius: 0.6rem;
  padding: 0.6rem;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  .name { font-size: 0.82rem; font-weight: 600; color: #1f2937; }
  .desc { font-size: 0.68rem; color: #6b7280; margin-top: 0.15rem; }
`;

const GoogleBtn = styled(motion.button)`
  width: 100%;
  padding: 0.75rem;
  border-radius: 0.7rem;
  border: 1.5px solid ${p => p.connected ? '#d1fae5' : '#e5e7eb'};
  background: ${p => p.connected ? '#ecfdf5' : 'white'};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.88rem;
  font-weight: 600;
  color: ${p => p.connected ? '#059669' : '#374151'};
  transition: all 0.15s;
`;

const DisconnectBtn = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.5rem;
  padding: 0;
`;

const SaveBtn = styled(motion.button)`
  width: 100%;
  padding: 0.8rem;
  border: none;
  border-radius: 0.7rem;
  background: linear-gradient(135deg, #334155, #475569);
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 0.5rem;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Toggle = styled.button`
  width: 48px; height: 28px;
  border-radius: 14px;
  border: none;
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
  background: ${p => p.active ? '#334155' : '#d1d5db'};
  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${p => p.active ? '23px' : '3px'};
    width: 22px; height: 22px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s;
    box-shadow: 0 1px 4px rgba(0,0,0,0.15);
  }
`;

const TestChatBtn = styled(motion.button)`
  width: 100%;
  margin-top: 0.5rem;
  padding: 0.7rem 0.8rem;
  border: 1.5px solid #075e54;
  background: white;
  color: #075e54;
  border-radius: 0.7rem;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background 0.15s;
  &:hover { background: #f0fdf4; }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  .label { font-size: 0.85rem; font-weight: 600; color: #374151; }
  .desc  { font-size: 0.72rem; color: #9ca3af; margin-top: 0.1rem; }
`;

// ─── COMPONENTE ───────────────────────────────────────────────────────────────
const Configuracoes = () => {
  const { user, token, login } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    storeName: '',
    ownerName: '',
    phone: '',
    workStart: '09:00',
    workEnd: '19:00',
    workDays: ['seg', 'ter', 'qua', 'qui', 'sex', 'sab'],
    breakTime: 0,
    aiName: 'Assistente',
    aiTone: 'simpatica',
    aiActive: true,
  });
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      storeName: user.storeName || '',
      ownerName: user.ownerName || '',
      phone:     user.phone || '',
      workStart: user.workingHours?.start || '09:00',
      workEnd:   user.workingHours?.end || '19:00',
      workDays:  user.workingHours?.days || ['seg', 'ter', 'qua', 'qui', 'sex', 'sab'],
      breakTime: user.breakTime || 0,
      aiName:    user.aiSettings?.assistantName || 'Assistente',
      aiTone:    user.aiSettings?.tone || 'simpatica',
      aiActive:  user.aiSettings?.active !== false,
    });
  }, [user]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/google/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { connected: false })
      .then(data => setGoogleStatus({ ...data, loading: false }))
      .catch(() => setGoogleStatus({ connected: false, loading: false }));
  }, [token]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      workDays: f.workDays.includes(day)
        ? f.workDays.filter(d => d !== day)
        : [...f.workDays, day]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/auth/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          storeName: form.storeName,
          ownerName: form.ownerName,
          phone: form.phone,
          workingHours: {
            start: form.workStart,
            end: form.workEnd,
            days: form.workDays,
          },
          breakTime: parseInt(form.breakTime) || 0,
          aiSettings: {
            assistantName: form.aiName,
            tone: form.aiTone,
            active: form.aiActive,
          },
        })
      });
      if (r.ok) {
        const updated = await r.json();
        login(token, updated);
        toast.success('Configurações salvas!');
      } else {
        toast.error('Erro ao salvar');
      }
    } catch {
      toast.error('Erro de conexão');
    }
    setSaving(false);
  };

  const handleConnectGoogle = async () => {
    try {
      const r = await fetch(`${API}/google/auth`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) {
        const { url } = await r.json();
        window.location.href = url;
      }
    } catch {
      toast.error('Erro ao conectar Google');
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Desconectar Google Agenda? Novos agendamentos não serão sincronizados.')) return;
    try {
      const r = await fetch(`${API}/google/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) {
        setGoogleStatus({ connected: false, loading: false });
        toast.success('Google desconectado');
      }
    } catch {
      toast.error('Erro ao desconectar');
    }
  };

  // Verificar se voltou do Google OAuth com resultado
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const google = params.get('google');
    if (google === 'conectado') {
      setGoogleStatus({ connected: true, loading: false });
      toast.success('Google Agenda conectado!');
      window.history.replaceState({}, '', '/configuracoes');
    } else if (google === 'erro') {
      toast.error('Erro ao conectar Google');
      window.history.replaceState({}, '', '/configuracoes');
    }
  }, []);

  return (
    <Page>
      <Header>
        <h1>Configurações</h1>
        <p>Gerencie seu estúdio e a IA</p>
      </Header>

      <Content>
        {/* Estúdio */}
        <Section>
          <SectionTitle>🏪 Meu estúdio</SectionTitle>
          <Field>
            <label>Nome do estúdio</label>
            <input value={form.storeName} onChange={e => set('storeName', e.target.value)} />
          </Field>
          <Field>
            <label>Seu nome</label>
            <input value={form.ownerName} onChange={e => set('ownerName', e.target.value)} />
          </Field>
          <Field>
            <label>Telefone / WhatsApp</label>
            <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(11) 99999-0000" />
          </Field>
        </Section>

        {/* Horário */}
        <Section>
          <SectionTitle>🕐 Horário de funcionamento</SectionTitle>
          <Row>
            <Field style={{ flex: 1 }}>
              <label>Abre às</label>
              <input type="time" value={form.workStart} onChange={e => set('workStart', e.target.value)} />
            </Field>
            <Field style={{ flex: 1 }}>
              <label>Fecha às</label>
              <input type="time" value={form.workEnd} onChange={e => set('workEnd', e.target.value)} />
            </Field>
          </Row>

          <Field>
            <label>Dias de atendimento</label>
            <DayChips>
              {DIAS.map(d => (
                <DayChip key={d.key} active={form.workDays.includes(d.key)} onClick={() => toggleDay(d.key)}>
                  {d.label}
                </DayChip>
              ))}
            </DayChips>
          </Field>

          <Field>
            <label>Intervalo entre atendimentos (minutos)</label>
            <input type="number" min="0" step="5" value={form.breakTime} onChange={e => set('breakTime', e.target.value)} />
          </Field>
        </Section>

        {/* IA */}
        <Section>
          <SectionTitle>🤖 Assistente IA</SectionTitle>

          <ToggleRow>
            <div>
              <div className="label">IA ativa</div>
              <div className="desc">A IA responde automaticamente no WhatsApp</div>
            </div>
            <Toggle active={form.aiActive} onClick={() => set('aiActive', !form.aiActive)} />
          </ToggleRow>

          <Field>
            <label>Nome da assistente</label>
            <input value={form.aiName} onChange={e => set('aiName', e.target.value)} placeholder="Ex: Luna, Sofia, Magzy" />
          </Field>

          <Field>
            <label>Tom de voz</label>
            <ToneGrid>
              {TONES.map(t => (
                <ToneCard key={t.key} active={form.aiTone === t.key} onClick={() => set('aiTone', t.key)}>
                  <div className="name">{t.label}</div>
                  <div className="desc">{t.desc}</div>
                </ToneCard>
              ))}
            </ToneGrid>
          </Field>

          <TestChatBtn
            type="button"
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/simular-atendimento')}
          >
            💬 Simular atendimento
          </TestChatBtn>
        </Section>

        {/* Google */}
        <Section>
          <SectionTitle>📅 Integrações</SectionTitle>

          {googleStatus.loading ? (
            <p style={{ fontSize: '0.85rem', color: '#9ca3af' }}>Verificando...</p>
          ) : googleStatus.connected ? (
            <div>
              <GoogleBtn connected={1} whileTap={{ scale: 0.98 }}>
                <span>📅</span> Google Agenda conectada
              </GoogleBtn>
              <DisconnectBtn onClick={handleDisconnectGoogle}>
                Desconectar Google Agenda
              </DisconnectBtn>
            </div>
          ) : (
            <GoogleBtn connected={0} whileTap={{ scale: 0.98 }} onClick={handleConnectGoogle}>
              <span>📅</span> Conectar Google Agenda
            </GoogleBtn>
          )}
        </Section>

        <SaveBtn
          whileTap={{ scale: 0.98 }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Salvando...' : 'Salvar configurações'}
        </SaveBtn>
      </Content>
    </Page>
  );
};

export default Configuracoes;
